import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api.js";

/**
 * Candidate · interview recording. Records the live camera + mic to WebM via MediaRecorder and
 * PUTs the bytes straight to Azure Blob Storage with a hand-signed, write-scoped service SAS the
 * API mints. It all degrades SILENTLY: if recording is disabled server-side, the browser lacks
 * MediaRecorder, or the mic is denied, the interview runs exactly the same — nothing here ever
 * throws or blocks the candidate, and a failed upload never holds up finishing the interview.
 *
 * WHY THIS UPLOADS IN SEGMENTS. It used to hold one recorder open for the whole interview and
 * upload once, at completion. Every byte therefore lived in a browser tab's memory until the very
 * end, so an interview that was closed, reloaded or crashed uploaded NOTHING — and the resumed
 * page then minted a fresh blob and a fresh interview_media row, which the report (newest row
 * wins) showed as if it were the whole session. A founder test session that spanned a browser
 * restart came back as its last 34 seconds, presented as complete. A recording that silently keeps
 * only the tail is worse than no recording, because the report implies full coverage.
 *
 * So the recorder is ROTATED every SEGMENT_MS: each run is stopped, which makes its WebM
 * self-contained and independently playable, and uploaded as its own blob while a replacement
 * recorder — started BEFORE the old one is stopped, so the handover overlaps rather than gaps —
 * carries on. The most a closed browser can now cost is the segment in flight. Each segment
 * registers its wall-clock duration, so the report can state the coverage it actually has instead
 * of implying it has all of it, and the player can scale a scrubber against a real number (a
 * MediaRecorder WebM carries no duration in its header).
 */

/** One recorder run per this long. Short enough that an abandoned interview loses little; long
 *  enough that a 20-minute interview is a handful of parts, not a hundred. */
const SEGMENT_MS = 120_000;
/** Don't rotate a segment that has barely started — a candidate flicking between tabs would
 *  otherwise mint a row per flick. */
const MIN_ROTATE_MS = 15_000;

interface RecordingInit {
  enabled: boolean;
  uploadUrl?: string;
  mediaId?: string;
  blobPath?: string;
}

/** One recorder run and the chunks it has produced. Chunks are per-run, never shared: two
 *  recorders overlap during a rotation and their bytes must not land in the same array. */
interface Segment {
  rec: MediaRecorder;
  chunks: Blob[];
  startedAt: number;
}

/** Pick a supported WebM profile; null falls back to the browser's default recorder mime. */
function pickMime(): string | null {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return null;
  const candidates = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  for (const c of candidates) if (MediaRecorder.isTypeSupported(c)) return c;
  return null;
}

export function useRecorder(
  token: string,
  stream: MediaStream | null,
): { start: () => void; finalize: () => Promise<void>; recording: boolean } {
  const [recording, setRecording] = useState(false);
  const segmentRef = useRef<Segment | null>(null);
  const combinedRef = useRef<MediaStream | null>(null);
  const mimeRef = useRef<string | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  /** An upload target already minted and not yet spent — the capability probe's own target, so
   *  proving recording is on doesn't burn a blob path. */
  const pendingTargetRef = useRef<RecordingInit | null>(null);
  const uploadedRef = useRef(0);
  const rotateTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedRef = useRef(false);
  const finalizedRef = useRef(false);
  const rotatingRef = useRef(false);
  /** The rotation in flight, so finalize can wait for it. Parts are ordered by their registration
   *  time, so a final segment that overtook the one before it would play back out of order. */
  const rotationRef = useRef<Promise<void> | null>(null);

  /** Mint (or spend the pre-minted) upload target and PUT one finished segment. Never throws. */
  const uploadSegment = useCallback(
    async (blob: Blob, durationMs: number): Promise<void> => {
      let target = pendingTargetRef.current;
      pendingTargetRef.current = null;
      if (!target) {
        try {
          target = await api.post<RecordingInit>(`/invite/${token}/interview/recording/init`, {
            segment: uploadedRef.current,
          });
        } catch {
          return; // API unreachable — this part is lost, the parts around it are not
        }
      }
      if (!target?.enabled || !target.uploadUrl || !target.mediaId || !target.blobPath) return;

      // One retry: a single dropped request mid-interview would otherwise punch a hole in the
      // middle of the recording, and the SAS is good for two hours either way.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          // Direct-to-Blob PUT with the write SAS. x-ms-blob-type is required to create a block blob.
          const put = await fetch(target.uploadUrl, {
            method: "PUT",
            headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": blob.type || "video/webm" },
            body: blob,
          });
          if (!put.ok) continue; // leave it unregistered rather than point at a missing blob
          await api.post(`/invite/${token}/interview/recording/complete`, {
            mediaId: target.mediaId,
            blobPath: target.blobPath,
            // Wall clock, not a container claim: this is what lets the report say how much of the
            // session is actually on tape, and the player scale a bar the WebM header can't.
            durationMs: Math.round(durationMs),
          });
          uploadedRef.current += 1;
          return;
        } catch {
          /* network hiccup — try once more, then let this part go */
        }
      }
    },
    [token],
  );

  /** Build and start a recorder over the combined stream. False when the browser refuses. */
  const beginSegment = useCallback((): boolean => {
    const combined = combinedRef.current;
    if (!combined) return false;
    const mime = mimeRef.current;
    let rec: MediaRecorder;
    try {
      rec = mime ? new MediaRecorder(combined, { mimeType: mime }) : new MediaRecorder(combined);
    } catch {
      return false;
    }
    const seg: Segment = { rec, chunks: [], startedAt: Date.now() };
    rec.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) seg.chunks.push(e.data);
    };
    try {
      // A timeslice keeps memory bounded within a segment and flushes data as it goes.
      rec.start(5_000);
    } catch {
      return false;
    }
    segmentRef.current = seg;
    return true;
  }, []);

  /** Stop one run and hand back its complete, playable blob. Null when it produced nothing. */
  const closeSegment = useCallback(
    (seg: Segment): Promise<{ blob: Blob; durationMs: number } | null> =>
      new Promise((resolve) => {
        const assemble = (): void => {
          const type = seg.rec.mimeType || mimeRef.current || "video/webm";
          const blob = new Blob(seg.chunks, { type });
          seg.chunks = [];
          resolve(blob.size > 0 ? { blob, durationMs: Math.max(0, Date.now() - seg.startedAt) } : null);
        };
        if (seg.rec.state === "inactive") {
          assemble();
          return;
        }
        seg.rec.onstop = assemble;
        try {
          seg.rec.stop();
        } catch {
          assemble();
        }
      }),
    [],
  );

  /**
   * Close the current segment, upload it, and keep recording. The replacement starts FIRST so the
   * two runs overlap by milliseconds instead of leaving a hole; if the replacement can't be built
   * the old recorder is left alone, because a failed rotation must never end the recording.
   */
  const rotate = useCallback((): Promise<void> => {
    if (rotatingRef.current) return rotationRef.current ?? Promise.resolve();
    if (finalizedRef.current) return Promise.resolve();
    const seg = segmentRef.current;
    if (!seg || seg.rec.state === "inactive") return Promise.resolve();
    if (Date.now() - seg.startedAt < MIN_ROTATE_MS) return Promise.resolve();
    rotatingRef.current = true;
    const run = (async () => {
      try {
        if (!beginSegment()) return; // keep the run we have
        const closed = await closeSegment(seg);
        if (closed) await uploadSegment(closed.blob, closed.durationMs);
      } finally {
        rotatingRef.current = false;
      }
    })();
    rotationRef.current = run;
    return run;
  }, [beginSegment, closeSegment, uploadSegment]);

  const start = useCallback((): void => {
    if (startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      // 1) Ask the API whether recording is on and, if so, get a write SAS. Never throw on failure.
      let init: RecordingInit;
      try {
        init = await api.post<RecordingInit>(`/invite/${token}/interview/recording/init`, { segment: 0 });
      } catch {
        return; // API unreachable / storage not configured — silently skip recording
      }
      if (!init?.enabled || !init.uploadUrl) return;
      pendingTargetRef.current = init;

      // 2) We need MediaRecorder and a camera stream to actually record.
      if (typeof MediaRecorder === "undefined" || !stream) return;

      // 3) The self-view camera is video-only (SpeechRecognition owns the mic elsewhere), so we
      //    grab an audio track just for the recording and merge it with the existing video track.
      //
      //    INVARIANT — this must stay the RAW camera track. The candidate's background-blur
      //    toggle (useBackgroundBlur) is a preview-only comfort layer: it composites onto a
      //    canvas over the self-view, or uses a cloned track for platform blur, and never
      //    mutates `stream`. Blur is not redaction — the recruiter reviews these frames, and
      //    presence/integrity review depends on seeing the real ones. Never swap a canvas
      //    captureStream() or a blurred clone in here.
      let audioTracks: MediaStreamTrack[] = [];
      try {
        const md = navigator.mediaDevices;
        if (md?.getUserMedia) {
          const mic = await md.getUserMedia({ audio: true });
          micStreamRef.current = mic;
          audioTracks = mic.getAudioTracks();
        }
      } catch {
        /* mic denied — record video-only rather than nothing */
      }
      combinedRef.current = new MediaStream([...stream.getVideoTracks(), ...audioTracks]);
      mimeRef.current = pickMime();

      if (!beginSegment()) return; // unsupported constraints — skip recording
      setRecording(true);
      rotateTimerRef.current = setInterval(() => void rotate(), SEGMENT_MS);
    })();
  }, [token, stream, beginSegment, rotate]);

  const finalize = useCallback(async (): Promise<void> => {
    if (finalizedRef.current) return; // idempotent — completion and unmount can both call this
    finalizedRef.current = true;

    if (rotateTimerRef.current) {
      clearInterval(rotateTimerRef.current);
      rotateTimerRef.current = null;
    }
    // Let a rotation that is already mid-flight register its part first — the report plays the
    // parts back in the order they were registered.
    await rotationRef.current?.catch(() => {});
    const seg = segmentRef.current;
    segmentRef.current = null;
    const closed = seg ? await closeSegment(seg) : null;
    setRecording(false);

    // Release the mic we opened for recording (the self-view camera is owned by useCameraPreview).
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    micStreamRef.current = null;
    combinedRef.current = null;

    if (closed) await uploadSegment(closed.blob, closed.durationMs);
  }, [closeSegment, uploadSegment]);

  /**
   * A hidden tab is the last warning we get before a candidate switches app, locks the phone or
   * closes the browser — and a tab killed in the background never runs a cleanup. Banking the
   * segment here is what turns "walked away and never came back" from a total loss into the loss
   * of one part. Best-effort by nature: if the tab dies mid-PUT, so does the PUT.
   */
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onHidden = (): void => {
      if (document.visibilityState === "hidden") void rotate();
    };
    document.addEventListener("visibilitychange", onHidden);
    return () => document.removeEventListener("visibilitychange", onHidden);
  }, [rotate]);

  // Safety net: if the interview screen unmounts before an explicit finalize (e.g. the server
  // jumps straight to "done"), still stop + upload. finalize() is idempotent so this is safe.
  useEffect(() => {
    return () => {
      void finalize();
    };
  }, [finalize]);

  return useMemo(() => ({ start, finalize, recording }), [start, finalize, recording]);
}
